import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check } from 'lucide-react';
import './ServicesSection.css';

// Pricing Plans Data
const plans = [
  {
    id: 1,
    name: "Basic",
    price: { range: "from ₹7,999", upfront: "50% upfront" },
    features: [
      "Landing page or simple website (up to 5 pages)",
      "Mobile responsive design",
      "Email support within 24 hrs",
      "Delivered in 7–10 business days",
      "Source code handover",
      "Basic documentation included"
    ],
    idealFor: "Small projects & startups",
    cta: "Book a Call",
    popular: false
  },
  {
    id: 2,
    name: "Standard",
    price: { range: "from ₹12,999", upfront: "50% upfront" },
    features: [
      "Everything in Basic",
      "Full website or web app (up to 10 pages)",
      "Advanced UI with animations",
      "Performance & SEO optimisation",
      "Priority support within 12 hrs",
      "Deployment assistance included",
      "Delivered in 14–21 business days"
    ],
    idealFor: "Growing businesses",
    cta: "Book a Call",
    popular: true
  },
  {
    id: 3,
    name: "Premium",
    price: { range: "from ₹18,999", upfront: "50% upfront" },
    features: [
      "Everything in Standard",
      "Custom full-stack or mobile app",
      "API integrations & third-party services",
      "Enterprise-grade security setup",
      "Complete source code & documentation",
      "3 months of ongoing maintenance",
      "24/7 dedicated support",
      "Performance monitoring included"
    ],
    idealFor: "Enterprise & large-scale projects",
    cta: "Book a Call",
    popular: false
  }
];

// PlanCard component
const PlanCard = ({ plan, index, isInView }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <motion.div
      ref={cardRef}
      className={`plan-card ${plan.popular ? 'plan-popular' : ''}`}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Spotlight Effect */}
      <div
        className="plan-spotlight"
        style={{
          background: isHovered
            ? `radial-gradient(400px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(250, 255, 0, 0.15), transparent 40%)`
            : 'none',
        }}
      />

      {plan.popular && (
        <div className="plan-badge">Most Popular</div>
      )}

      <div className="plan-content">
        <h3 className="plan-name">{plan.name}</h3>

        {/* Pricing */}
        <div className="plan-price">
          <span className="plan-price-range">{plan.price.range}</span>
          <span className="plan-price-upfront">{plan.price.upfront}</span>
        </div>

        <div className="plan-features">
          <ul className="plan-features-list">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="plan-feature-item">
                <Check size={18} className="plan-check-icon" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="plan-ideal">
          <span className="plan-ideal-label">Ideal for:</span>
          <span className="plan-ideal-text">{plan.idealFor}</span>
        </div>

        {/* CTA Button */}
        <a
          href="/contact"
          className={`plan-cta ${plan.popular ? 'plan-cta-popular' : ''}`}
        >
          {plan.cta}
        </a>
      </div>
    </motion.div>
  );
};

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section ref={sectionRef} className="services-section">
      <div className="services-container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="services-title">Our Plans</h2>
          <p className="services-subtitle">
            Choose the perfect plan tailored to your project needs and scale
          </p>
        </motion.div>

        <div className="plans-grid">
          {plans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Upfront note */}
        <motion.p
          className="plans-note"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          All plans require 50% payment upfront. Remaining 50% on delivery.
        </motion.p>
      </div>
    </section>
  );
};

export default ServicesSection;